import { captureException } from '@sentry/react';
import * as appSelectors from 'app/state/app/app-selectors';
import { appActions } from 'app/state/app/app-slice';
import { dashboardActions } from 'app/state/dashboard/dashboard-slice';
import * as loginSelectors from 'app/state/login/login-selectors';
import { loginActions } from 'app/state/login/login-slice';
import { payloadActions } from 'app/state/payload/payload-slice';
import { signIn, confirmSignIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import last from 'lodash/last';
import {
  PortalAuthCustomChallengeError,
  PortalAuthLoginError,
  PortalAuthSignOutError,
} from 'login/errors';
import { useCallback } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Alert } from 'rsuite';
import { authSender } from 'utils/auth-sender';
import { captureExceptionWithContext } from 'utils/sentry-functions';

export const USER_AUTH_SUCCESSFUL_MESSAGE = 'USER_SRP_AUTH';

export function getUserIdFromCognitoUserName(username) {
  /**
   * Users are in the format of "<PLACE>::userid/<ID>"
   * e.g. "tx-galveston-city_of_galveston::userid/1"
   */
  return last(username.split('/'));
}

// ! Important
// ! Ensure that "authenticateCurrentUser" in "App.jsx" is up-to-date with this custom hook
export function usePortalAuth() {
  const dispatch = useDispatch();

  const jurisdictionPlaceString = useSelector(appSelectors.place);

  const reduxStoredCognitoEmailAddress = useSelector(loginSelectors.userEmail);
  const reduxStoredCognitoUserName = useSelector(
    loginSelectors.loggedInUsername,
  );

  /**
   * This function is responsible for handling:
   *  1. initiating the login process
   *  2. handling the MFA code
   */
  const initiateUserAuthenticationProcess = useCallback(
    async (useInternalErrorHandling = true) => {
      try {
        if (!reduxStoredCognitoEmailAddress || !reduxStoredCognitoUserName) {
          throw new Error('No Cognito username or email provided.');
        }

        const signInOutput = await signIn({
          username: reduxStoredCognitoUserName,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        });

        if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          return await confirmSignIn({
            challengeResponse: reduxStoredCognitoEmailAddress,
          });
        }

        return signInOutput;
      } catch (err) {
        if (useInternalErrorHandling)
          captureExceptionWithContext(new PortalAuthLoginError(err), {
            errorMsg:
              'Error when attempting to initiate user authentication process.',
            place: jurisdictionPlaceString,
            emailAddress: reduxStoredCognitoEmailAddress,
            username: reduxStoredCognitoUserName,
          });

        if (useInternalErrorHandling)
          Alert.error(
            'There was an issue while attempting to initiate the login process. Please try again at a later time.',
            10000,
          );

        throw err;
      }
    },
    [
      jurisdictionPlaceString,
      reduxStoredCognitoEmailAddress,
      reduxStoredCognitoUserName,
    ],
  );

  /**
   * This is update the Redux state leading to the rendering of "PortalCognitoAuthModal",
   * which will take over guiding the user through the login and authentication process.
   */
  const dispatchInitialUserLoginFlowActions = useCallback(
    (userEmail, user) => {
      const username = `${jurisdictionPlaceString}::userid/${user.userId}`;
      batch(() => {
        dispatch(loginActions.setOpenLoginModal(true));
        dispatch(loginActions.setUserEmail(userEmail));
        dispatch(payloadActions.setLookupModeEmail(userEmail));
        dispatch(loginActions.setLoggedInUsername({ username }));
        dispatch(loginActions.setUserAccountDetails(user));
      });
    },
    [dispatch, jurisdictionPlaceString],
  );

  const dispatchUserSignOutCleanUpActions = useCallback(() => {
    batch(() => {
      dispatch(appActions.setVisitedFirstPageInFlow({ value: false }));

      dispatch(loginActions.resetLogin());

      dispatch(payloadActions.resetFlow());

      dispatch(dashboardActions.resetDashboard());

      // TODO: Add the other slice resets as more and more features from the reg system is made compatible with Customer Portal
    });
  }, [dispatch]);

  const userSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (err) {
      captureException(new PortalAuthSignOutError(err));
      window.localStorage.clear();
    } finally {
      dispatchUserSignOutCleanUpActions();
    }
  }, [dispatchUserSignOutCleanUpActions]);

  const dispatchUserSignInActions = useCallback(
    async () => {
      const { tokens } = await fetchAuthSession();
      const idTokenPayload = tokens?.idToken?.payload;
      const username = idTokenPayload?.['cognito:username'] || '';
      const email = idTokenPayload?.email || '';

      const isUserCognitoAuthenticated = !!tokens?.idToken;

      dispatch(
        loginActions.setCognitoUserDetails({
          isUserCognitoAuthenticated,
          username,
          email,
        }),
      );

      if (isUserCognitoAuthenticated) {
        const { data: user } = await authSender.get(
          `/getUserByEmailAuth/${email}`,
        );
        dispatch(loginActions.setUserAccountDetails(user));
      }

    dispatch(loginActions.setOpenLoginModal(false));
  }, [dispatch]);

  /**
   * This function is for handling just the interaction of sending the MFA code
   * to AWS Cognito.
   */
  const handleCustomChallengeResponse = useCallback(
    async (currentCognitoUserRef, challengeResponse, invalidAnswerCB) => {
      try {
        const confirmOutput = await confirmSignIn({
          challengeResponse,
        });

        if (!confirmOutput.isSignedIn) {
          return invalidAnswerCB();
        }

        await dispatchUserSignInActions(confirmOutput);
      } catch (err) {
        captureExceptionWithContext(new PortalAuthCustomChallengeError(err), {
          errorMsg:
            'Error when attempting to verify an MFA code during account creation process',
          place: jurisdictionPlaceString,
          cognitoUserName: reduxStoredCognitoUserName,
          cognitoEmailAddress: reduxStoredCognitoEmailAddress,
        });

        Alert.error(
          'There was an issue with verifying your MFA code. Please try again at a later time.',
          10000,
        );

        throw err;
      }
    },
    [
      dispatchUserSignInActions,
      jurisdictionPlaceString,
      reduxStoredCognitoEmailAddress,
      reduxStoredCognitoUserName,
    ],
  );

  return {
    initiateUserAuthenticationProcess,
    dispatchInitialUserLoginFlowActions,
    dispatchUserSignOutCleanUpActions,
    userSignOut,
    handleCustomChallengeResponse,
  };
}
